/* eslint-disable camelcase */
import * as yup from "yup"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import { Appearance, TextInput, View, ViewStyle } from "react-native"
import { Cycle, Farm, Stock } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormControl } from "../../form-control/form-control"
import { FormProps } from "../form"
import { Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Radio } from "native-base"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { useStores } from "../../../models"
import { CycleProFeature } from "../../cycle-pro-feature/cycle-pro-feature"
import _ from "lodash"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

export interface TreatmentFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    pond_id?: number
    cycle_id?: number
    stock_id?: number
    name?: string
    treated_at?: string
    quantity?: number
    remark?: string
  }
}

/**
 * Describe your component here
 */
export const TreatmentForm = observer(function TreatmentForm(props: TreatmentFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit,
    colorScheme = "light" || Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const {
    appStore: { farmStore }
  } = useStores();

  const [ cycle, setCycle ] = useState<any>(null)
  const [ recordType, setRecordType ] = useState<string>(() => (
    initialValuesProps.stock_id ? 'pro' : 'basic'
  ));
  const [ openCycleProFeature, setOpenCycleProFeature ] = useState<boolean>(false)

  useEffect(() => {
    const cycle = farmStore.cycles.find((c) => c.id === initialValuesProps.cycle_id)
    setCycle(cycle)
  }, [farmStore.cycles, initialValuesProps.cycle_id])

  const { pro } = cycle || {}

  const input = {
    quantity: useRef<TextInput>(),
    remark: useRef<TextInput>(),
  }

  const nextInput = {
    quantity: () => input.remark.current && input.remark.current.focus(),
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("treatmentForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("treatmentForm.cycleId")),
    stock_id: recordType === 'basic'
      ? yup.number().nullable().label(translate("treatmentForm.stockId"))
      : yup.number().nullable().required().label(translate("treatmentForm.stockId")),
    name: yup.string().ascii().max(255).nullable().required().label(translate("treatmentForm.name")),
    treated_at: yup.mixed().nullable().required().label(translate("treatmentForm.treatedAt")),
    quantity: yup.number().min(0).max(1000).nullable().label(translate("treatmentForm.quantity")),
    remark: yup.string().ascii().max(255).nullable().label(translate("treatmentForm.remark")),
  })

  const initialValues = useMemo<TreatmentFormProps["initialValues"]>(() => ({
    farm_id: null,
    pond_id: null,
    treated_at: moment().format('YYYY-MM-DD'),
    quantity: null,
    ...initialValuesProps
  }), [initialValuesProps])

  const onChangeRecordType = useCallback((type: "pro"|"basic") => {
    if (pro) setRecordType(type);
    else if (type === 'pro') setOpenCycleProFeature(true);
  }, [pro])

  const onCloseCycleProFeature = useCallback(() => {
    setOpenCycleProFeature(false);
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, initialValues, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="treatmentForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('stock_id', null)
                await setFieldValue('cycle_id', null)
                await setFieldValue('pond_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id')
              }
            }}
          />
          <View style={TITLE}>
            <Text tx="treatmentForm.treatment" preset="title3" colorScheme={colorScheme} />
          </View>
          <View style={BORDER} />
          <WalkthroughElement
            id="treatment-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="treatmentForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  Promise.resolve(_.orderBy(
                    farmStore.cycles.map((cycle) => Cycle.hydrate(cycle)), ['started_at', 'pond_name_alpha_numeric'], ['desc', 'asc']
                  )).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  await setCycle(item && item.toArray() || null)
                  await setFieldValue('pond_id', (item || {}).pond_id || null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "treatmentForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="treated_at"
              txPrefix="treatmentForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <FormControl
              key={`${values.cycle_id}-pro-${pro ? '1' : '0'}`}
              labelTx="treatmentForm.recordType"
              colorScheme={colorScheme}
              labelColor={color.palette.gray800}
              style={FORM_CONTROL}
            >
              <Radio.Group
                name="pro"
                colorScheme={colorScheme}
                accessibilityLabel="pro"
                value={recordType}
                onChange={onChangeRecordType}
              >
                <Radio value="basic" mb="8px" isDisabled={initialValues.stock_id}>
                  <Text tx="treatmentForm.radioBasic" preset="body2" mx="8px" colorScheme={colorScheme} />
                </Radio>
                <Radio value="pro" isDisabled={initialValues.stock_id}>
                  <Text tx="treatmentForm.radioPro" preset="body2" mx="8px" colorScheme={colorScheme} />
                </Radio>
              </Radio.Group>
              <CycleProFeature
                isOpen={openCycleProFeature}
                onClose={onCloseCycleProFeature}
                allow={pro}
                colorScheme={colorScheme}
              />
            </FormControl>
            { recordType === "pro" ? <>
                <ModelField
                  key={`${values.farm_id}-stock_id`}
                  name="stock_id"
                  txPrefix="treatmentForm"
                  colorScheme={colorScheme}
                  formControlProps={{ isRequired: true }}
                  modelPickerProps={{
                    Model: Stock,
                    dataLabel: "product_name",
                    fetchData: (model, search) => (
                      model.fetch({
                        farm_id__eq: values.farm_id,
                        product_category_id__ne: 123,
                        search,
                        sort: 'product_name'
                      })
                    ),
                    renderSelectedItemText: (item) => item && `${item.product_name} ${item.product_unit && `(${item.product_unit})` || ''}`,
                    onValueChange: async (value, index, item) => {
                      await setFieldValue('name', (item && `${item.product_name} (${item.product_unit})`) || null)
                      await setFieldValue('stock_id', value || null)
                      await setFieldTouched('stock_id')
                    },
                    clearable: true,
                    isDisabled: !values.farm_id
                  }}
                />
                <NumeralField
                  name="quantity"
                  txPrefix="treatmentForm"
                  colorScheme={colorScheme}
                  input={input}
                  nextInput={nextInput}
                />
              </> : <TextField
                name="name"
                txPrefix="treatmentForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput}
                formControlProps={{ isRequired: true }}
              />}
            <TextField
              name="remark"
              txPrefix="treatmentForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

TreatmentForm.displayName = 'TreatmentForm'
